/**
 * More Menu Component
 * Dropdown menu with secondary message actions
 */

import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import type { Message } from '@/types/api.types';

interface MoreMenuProps {
  message: Message;
  isOwnMessage: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onForward?: () => void;
}

const MoreMenu = ({ message, isOwnMessage, onEdit, onDelete, onForward }: MoreMenuProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      toast.success('Текстът е копиран');
      setShowMenu(false);
    } catch (error) {
      toast.error('Грешка при копиране на текст');
      console.error('Failed to copy text:', error);
    }
  };

  const handleCopyLink = async () => {
    try {
      const url = window.location.origin + window.location.pathname + `?message=${message.id}`;
      await navigator.clipboard.writeText(url);
      toast.success('Линкът е копиран');
      setShowMenu(false);
    } catch (error) {
      toast.error('Грешка при копиране на линк');
      console.error('Failed to copy link:', error);
    }
  };

  const handleEdit = () => {
    onEdit?.();
    setShowMenu(false);
  };

  const handleDelete = () => {
    onDelete?.();
    setShowMenu(false);
  };

  const handleForward = () => {
    onForward?.();
    setShowMenu(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="p-2 text-gray-500 hover:text-gray-700 rounded-lg transition-colors"
        title="Още опции"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {showMenu && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
          {/* Edit (own messages only) */}
          {isOwnMessage && onEdit && (
            <button
              onClick={handleEdit}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              <span>Редактирай</span>
            </button>
          )}

          {/* Delete (own messages only) */}
          {isOwnMessage && onDelete && (
            <button
              onClick={handleDelete}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span>Изтрий</span>
            </button>
          )}

          {/* Divider (if own message actions exist) */}
          {isOwnMessage && (onEdit || onDelete) && (
            <div className="border-t border-gray-200 my-1" />
          )}

          {/* Forward */}
          {onForward && (
            <button
              onClick={handleForward}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
              <span>Препрати</span>
            </button>
          )}

          {/* Copy Text */}
          <button
            onClick={handleCopyText}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>Копирай текст</span>
          </button>

          {/* Copy Link */}
          <button
            onClick={handleCopyLink}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            <span>Копирай линк</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default MoreMenu;
